import { useRef, useImperativeHandle, forwardRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import Button from './ui/Button';
import { RotateCcw } from 'lucide-react';

interface SignatureCanvasProps {
  onSignatureChange?: (signature: string | null) => void;
  className?: string;
}

export interface SignatureCanvasRef {
  clear: () => void;
  isEmpty: () => boolean;
  getSignatureData: () => string | null;
}

const SignatureCanvasComponent = forwardRef<SignatureCanvasRef, SignatureCanvasProps>(
  ({ onSignatureChange, className }, ref) => {
    const sigCanvasRef = useRef<SignatureCanvas>(null);
    const [isCanvasEmpty, setIsCanvasEmpty] = useState(true);

    useImperativeHandle(ref, () => ({
      clear: () => {
        if (sigCanvasRef.current) {
          sigCanvasRef.current.clear();
          onSignatureChange?.(null);
          setIsCanvasEmpty(true);
        }
      },
      isEmpty: () => {
        return sigCanvasRef.current?.isEmpty() ?? true;
      },
      getSignatureData: () => {
        if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
          return sigCanvasRef.current.toDataURL('image/png');
        }
        return null;
      },
    }));

    const handleClear = () => {
      if (sigCanvasRef.current) {
        sigCanvasRef.current.clear();
        onSignatureChange?.(null);
        setIsCanvasEmpty(true);
      }
    };

    const handleSignatureEnd = () => {
      if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
        const signatureData = sigCanvasRef.current.toDataURL('image/png');
        onSignatureChange?.(signatureData);
        setIsCanvasEmpty(false);
      }
    };

    return (
      <div className={`relative ${className}`}>
        <div className="relative border-2 border-dashed border-gray-300 rounded-lg bg-white">
          <SignatureCanvas
            ref={sigCanvasRef}
            onEnd={handleSignatureEnd}
            clearOnResize={false}
            canvasProps={{
              className: 'w-full h-48 rounded-lg touch-none',
              style: {
                width: '100%',
                height: '192px',
                touchAction: 'none',
              },
            }}
            penColor="black"
            backgroundColor="rgba(255, 255, 255, 1)"
            dotSize={1}
            minWidth={1}
            maxWidth={3}
            throttle={10}
            velocityFilterWeight={0.7}
          />
          {isCanvasEmpty && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <span className="text-gray-300 font-semibold tracking-wide uppercase">
                Firma aquí
              </span>
            </div>
          )}
        </div>

        <div className="mt-4 flex justify-between items-center">
          <p className="text-sm text-gray-600">
            Firma aquí con tu dedo o stylus
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleClear}
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Limpiar
          </Button>
        </div>
      </div>
    );
  }
);

SignatureCanvasComponent.displayName = 'SignatureCanvas';

export default SignatureCanvasComponent;
